import { useRef } from 'react';
import { parseMemoryDate } from 'helpers/memories';
import { memoryDateLabel } from 'helpers/memory-date-label';
import { ContentThumbnail } from 'common/content-thumbnail';
import { MemberAvatar } from 'common/member-avatar';
import { classnames } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './memory-card.module.scss';

const MAX_TILT = 5; // degrees

interface MemoryCardProps {
  content: AlbumContentSerialized;
  onClick?: () => void;
  // When set (Shared With Me view), shows who shared this memory, bottom-right.
  sharedBy?: { name: string };
  // Replaces the date shown under the title, for a screen that places the
  // memory by a date of its own (the timeline).
  dateLabel?: string;
  // Not in any album (it lives in the vault's hidden default album).
  unsorted?: boolean;
  // Drag support — lets the memory be dragged onto an album to copy it there.
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLButtonElement>) => void;
  isDragging?: boolean;
}

const formatMemoryDate = (value?: string | null) => {
  const date = parseMemoryDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const MemoryCard = ({
  content,
  onClick,
  sharedBy,
  dateLabel,
  unsorted = false,
  draggable,
  onDragStart,
  onDragEnd,
  isDragging,
}: MemoryCardProps) => {
  const cardRef = useRef<HTMLButtonElement>(null);
  const date =
    dateLabel ??
    // The date set on the memory first, as the lists are sorted by it, and
    // as it was entered (a range, or just a year or month).
    (memoryDateLabel(content.startDate, content.endDate) ||
      formatMemoryDate(
        content.startDate || content.fileDate || content.createdAt,
      ));

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    el.style.setProperty('--ry', `${(px - 0.5) * 2 * MAX_TILT}deg`);
    el.style.setProperty('--rx', `${(0.5 - py) * 2 * MAX_TILT}deg`);
    el.style.setProperty('--mx', `${px * 100}%`);
    el.style.setProperty('--my', `${py * 100}%`);
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  return (
    <button
      ref={cardRef}
      className={classnames(styles.memoryCard, {
        [styles.dragging]: !!isDragging,
      })}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      type="button"
      // Where the returning-customer introduction lands its pictures.
      data-memory-id={content.id}
    >
      <div className={styles.thumbnailContainer} data-handoff-memory>
        {/* Shared thumbnail logic — same component used by the album grid and
            the Shared With Me view (image / video frame / typed-icon fallback). */}
        <ContentThumbnail
          content={content}
          className={styles.thumb}
          showVideoOverlay
        />
        <span className={styles.glare} />
        {unsorted && <span className={styles.unsortedChip}>Without Album</span>}
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.contentText}>
          <span className={styles.title}>{content.name || 'Untitled'}</span>
          {date && <p className={styles.date}>{date}</p>}
        </div>
        {sharedBy && (
          <MemberAvatar
            name={sharedBy.name}
            size={32}
            className={styles.sharerAvatar}
          />
        )}
      </div>
    </button>
  );
};
